import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Card, Badge, Button } from '../ui';
import api from '../../services/api';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * AgentProjectsManager
 *
 * Renders the list of projects an agent is currently linked to and lets the
 * operator attach / detach without leaving Agent Detail. Visual contract:
 *
 *  - Each row: project name + linked-at + small badge `N msgs` (= the agent's
 *    observed messages in chats belonging to this project; populated by the
 *    server via AgentProjectService.getAgentProjects).
 *  - Detach = small × icon on hover.
 *  - Add = project selector dropdown that lists the agent's CURRENTLY
 *    AVAILABLE projects (existing helper: GET /backend/projects/:id/agents/available).
 *    After attach: row appears; after detach: row disappears.
 *
 * React Query keys:
 *   - ['agent', agentId, 'projects']        -> linked projects
 *   - ['agent', agentId, 'available']       -> available projects to attach
 */
const AgentProjectsManager = ({ agentId, agentName }) => {
  const queryClient = useQueryClient();
  const [showAttach, setShowAttach] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [availableSearch, setAvailableSearch] = useState('');

  const projectsQuery = useQuery(
    ['agent', agentId, 'projects'],
    () =>
      api
        .get(`/agents/${agentId}/projects`, { params: { activeOnly: true } })
        .then((res) => res.data?.projects || []),
    { enabled: Boolean(agentId), keepPreviousData: true }
  );

  const availableQuery = useQuery(
    ['agent', agentId, 'available', availableSearch],
    () => {
      if (!selectedProjectId) return [];
      // The available endpoint is keyed by project, not by agent. We use
      // a generic search across all agents belonging to that project — but
      // since we're listing attachable projects (not agents), we hit the
      // /projects endpoint to enumerate and filter client-side.
      return api
        .get('/projects', { params: { search: availableSearch || undefined, limit: 200 } })
        .then((res) => res.data?.projects || res.data || []);
    },
    { enabled: showAttach, keepPreviousData: true }
  );

  const linkMutation = useMutation(
    (projectId) =>
      api
        .post(`/projects/${projectId}/agents/link`, { agentId: Number(agentId) })
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent', agentId, 'projects']);
        queryClient.invalidateQueries(['agentStats']);
        setSelectedProjectId('');
        setShowAttach(false);
      },
    }
  );

  const unlinkMutation = useMutation(
    ({ projectId }) =>
      api
        .delete(`/projects/${projectId}/agents/${agentId}/unlink`)
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agent', agentId, 'projects']);
        queryClient.invalidateQueries(['agentStats']);
      },
    }
  );

  const handleUnlink = (projectId) => {
    if (window.confirm(`Detach this agent from the project?`)) {
      unlinkMutation.mutate({ projectId });
    }
  };

  const projects = projectsQuery.data || [];
  const available = (availableQuery.data || []).filter(
    (p) => !projects.some((linked) => linked.id === p.id)
  );

  return (
    <Card>
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Projects</h2>
            <p className="text-xs text-gray-500">
              Projects this agent is linked to. Used for activity attribution in the global Messages feed.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => setShowAttach(!showAttach)}
            disabled={linkMutation.isLoading}
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            {showAttach ? 'Cancel' : 'Attach to project'}
          </Button>
        </div>

        {showAttach && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Search project
            </label>
            <input
              type="text"
              value={availableSearch}
              onChange={(e) => setAvailableSearch(e.target.value)}
              placeholder="Type to filter…"
              className="mb-2 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
              disabled={availableQuery.isLoading}
            >
              <option value="">— select a project —</option>
              {available.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <div className="mt-2 flex justify-end gap-2">
              <Button
                variant="primary"
                disabled={!selectedProjectId || linkMutation.isLoading}
                onClick={() => linkMutation.mutate(Number(selectedProjectId))}
              >
                {linkMutation.isLoading ? 'Linking…' : 'Link'}
              </Button>
            </div>
            {availableQuery.isLoading && (
              <p className="mt-1 text-xs text-gray-500">Loading projects…</p>
            )}
            {available.length === 0 && !availableQuery.isLoading && (
              <p className="mt-2 text-xs text-gray-500">
                No additional projects available.
              </p>
            )}
          </div>
        )}

        {projectsQuery.isLoading ? (
          <p className="text-sm text-gray-500">Loading projects…</p>
        ) : projects.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
            Not linked to any project yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {projects.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/projects/${p.id}`}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {p.name}
                  </Link>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span>
                      linked{' '}
                      {p.linked_at
                        ? new Date(p.linked_at).toLocaleDateString()
                        : '—'}
                    </span>
                    {Number(p.messages_in_project) > 0 ? (
                      <Badge size="sm" variant="purple">
                        {p.messages_in_project} msgs
                      </Badge>
                    ) : null}
                    {p.last_message_in_project ? (
                      <span className="text-gray-400">
                        last:{' '}
                        {new Date(p.last_message_in_project).toLocaleDateString()}
                      </span>
                    ) : null}
                  </div>
                </div>
                <button
                  onClick={() => handleUnlink(p.id)}
                  disabled={unlinkMutation.isLoading}
                  className="ml-2 rounded-full p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                  title="Detach"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {agentName ? (
          <p className="mt-3 text-xs text-gray-400">
            Linked projects appear as project badges in the global Messages feed for messages from <span className="font-mono">{agentName}</span>.
          </p>
        ) : null}
      </div>
    </Card>
  );
};

export default AgentProjectsManager;